import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ShieldCheck, Check } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { serviceGroups } from "../data/services";
import { legal, has, officeOneLine, OUTCOME_DISCLAIMER } from "../data/legal";
import { submitContact, buildConsent } from "../config/maraApi";

export default function Contact() {
  const address = officeOneLine();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    visaGroup: "",
    message: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !privacy) return;
    setStatus("submitting");
    setError("");
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        visaGroup: form.visaGroup,
        message: form.message,
        consent: buildConsent({ privacyAccepted: privacy }),
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description={`Contact ${legal.brandName}, a migration support and coordination service. Talk to us about getting application-ready and being matched with a registered migration agent.`}
        path="/contact/"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />

      <section className="container-page py-12">
        <p className="eyebrow">Get in touch</p>
        <h1 className="mt-3 text-4xl font-bold text-acblue sm:text-5xl">
          Contact us
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-acBlack/70">
          Tell us about your situation and we will help you get application-ready
          and match you with a registered migration agent for the regulated
          advice and lodgement. We stay your single point of contact and keep the
          matter moving.
        </p>
      </section>

      <section className="bg-acBg py-16">
        <div className="container-page grid gap-10 lg:grid-cols-3">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="card">
              <h2 className="text-xl font-bold text-acblue">
                Send us a message
              </h2>
              <p className="mt-2 text-sm text-acBlack/70">
                We will get back to you about your enquiry. There is no
                obligation.
              </p>

              {status === "success" ? (
                <div className="mt-6 rounded-2xl bg-acNavySoft/60 p-6 text-center">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-acblue text-white">
                    <Check className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 font-semibold text-acblue">
                    Thanks, we have your message.
                  </h3>
                  <p className="mt-1 text-sm text-acBlack/70">
                    We will be in touch about your enquiry.
                  </p>
                </div>
              ) : (
                <form className="mt-6 grid gap-4 sm:grid-cols-2" onSubmit={submit}>
                  <div>
                    <label className="text-sm font-medium text-acblue">Full name</label>
                    <input
                      className="mt-1 w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-acblue">Email</label>
                    <input
                      type="email"
                      className="mt-1 w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                      placeholder="you@email.com"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-acblue">Phone</label>
                    <input
                      className="mt-1 w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                      placeholder="Optional"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-acblue">
                      Visa of interest
                    </label>
                    <select
                      className="mt-1 w-full rounded-xl border border-acblue/15 bg-white px-4 py-3 text-sm outline-none focus:border-acAccent"
                      value={form.visaGroup}
                      onChange={(e) => set("visaGroup", e.target.value)}
                    >
                      <option value="">Not sure yet</option>
                      {serviceGroups.map((g) => (
                        <option key={g.slug} value={g.slug}>
                          {g.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-sm font-medium text-acblue">
                      How can we help?
                    </label>
                    <textarea
                      rows={4}
                      className="mt-1 w-full rounded-xl border border-acblue/15 px-4 py-3 text-sm outline-none focus:border-acAccent"
                      placeholder="Tell us a little about your situation"
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      required
                    />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-acBlack/70 sm:col-span-2">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={privacy}
                      onChange={(e) => setPrivacy(e.target.checked)}
                      required
                    />
                    <span>
                      I have read the{" "}
                      <Link to="/privacy-policy/" className="text-acAccent underline">
                        Privacy Policy
                      </Link>{" "}
                      and consent to Migration & More collecting and using my personal
                      information to respond to my enquiry.
                    </span>
                  </label>
                  {status === "error" && (
                    <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 sm:col-span-2">
                      {error}
                    </p>
                  )}
                  <div className="sm:col-span-2">
                    <button
                      type="submit"
                      className="btn-primary w-full sm:w-auto"
                      disabled={status === "submitting"}
                    >
                      {status === "submitting" ? "Sending..." : "Send message"}
                    </button>
                    <p className="mt-3 text-xs text-acBlack/50">{OUTCOME_DISCLAIMER}</p>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Contact details */}
          <aside className="space-y-4">
            <div className="card">
              <h2 className="text-lg font-semibold text-acblue">
                Speak to us directly
              </h2>
              <ul className="mt-4 space-y-4 text-sm">
                {has(legal.phone) && (
                  <li className="flex items-start gap-3">
                    <Phone className="mt-0.5 h-5 w-5 shrink-0 text-acAccent" />
                    <a
                      href={`tel:${legal.phone}`}
                      className="text-acBlack/80 hover:text-acblue"
                    >
                      {legal.phone}
                    </a>
                  </li>
                )}
                {has(legal.email) && (
                  <li className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-acAccent" />
                    <a
                      href={`mailto:${legal.email}`}
                      className="text-acBlack/80 hover:text-acblue"
                    >
                      {legal.email}
                    </a>
                  </li>
                )}
                {has(address) && (
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-acAccent" />
                    <span className="text-acBlack/80">{address}</span>
                  </li>
                )}
                <li className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-5 w-5 shrink-0 text-acAccent" />
                  <span className="text-acBlack/80">{legal.hours}</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3 rounded-2xl bg-white p-5 ring-1 ring-acblue/5">
              <ShieldCheck className="h-5 w-5 shrink-0 text-acAccent" />
              <p className="text-xs text-acBlack/60">
                We only work with registered agents, bound by the Code of
                Conduct and professionally insured, and we keep your matter
                moving.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
